import type { SupabaseClient } from "@supabase/supabase-js";
import { useQuery } from "@supabase-cache-helpers/postgrest-react-query";
import { useInfiniteQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import {
  DEFAULT_ASCENDING,
  DEFAULT_INFINITE_LIMIT,
  DEFAULT_LIMIT,
  DEFAULT_WILDCARD_CHAR,
} from "@/configs";
import { useSupabase } from "@/hooks/use-supabase";
import type {
  ConstraintOwner,
  Database,
  FilterClause,
  FilterExpression,
  MakeUseCountResult,
  MakeUseDetailsResult,
  MakeUseInfiniteResult,
  MakeUseListResult,
  OneOrMany,
  OrderBy,
  QueryOptions,
  Range,
  Scalar,
  SearchClause,
  SelectQuery,
  Tables,
  UnknownRecord,
} from "@/types";

const MAX_SEARCH_TERM_LENGTH = 1000;

// Type guards for column specs
function isColumnAliasSpec(value: unknown): value is {
  column: string | number | symbol;
  as?: string;
} {
  return (
    typeof value === "object" &&
    value !== null &&
    "column" in (value as UnknownRecord) &&
    !("table" in (value as UnknownRecord))
  );
}

// Type guard for embedded table specs (foreign table joins)
function isEmbedSpec(value: unknown): value is {
  table: string;
  as?: string;
  columns?: Array<string | { column: string; as?: string }> | "*";
  constraint?: string;
  constraintField?: string;
  constraintOwner?: ConstraintOwner;
} {
  return (
    typeof value === "object" &&
    value !== null &&
    "table" in (value as UnknownRecord)
  );
}

// Check if a value matches the Range shape
function isRange(value: unknown): value is Range {
  return (
    !!value &&
    typeof value === "object" &&
    "from" in (value as UnknownRecord) &&
    "to" in (value as UnknownRecord)
  );
}

// Escape characters used by SQL LIKE and PostgREST filter syntax
function escapeSqlLikePattern(value: string): string {
  return value
    .replace(/\\/g, "\\\\")
    .replace(/"/g, '\\"')
    .replace(/_/g, "\\_")
    .replace(/%/g, "\\%");
}

// Normalize a LIKE/ILIKE search term
function normalizeLike(
  value: string,
  opts?: {
    allowWildcards?: boolean;
    wildcardChar?: string;
    addContains?: boolean;
  },
): string {
  const allow = opts?.allowWildcards ?? true;
  const wc = opts?.wildcardChar ?? DEFAULT_WILDCARD_CHAR;
  const escaped = escapeSqlLikePattern(value);

  const ESC_STAR = "\u0001";
  const ESC_WC = "\u0002";
  let s = escaped.replace(/\\\*/g, ESC_STAR);
  if (wc !== "*")
    s = s.replace(
      new RegExp(`\\\\${wc.replace(/[-/\\^$*+?.()|[\]{}]/g, "\\$&")}`, "g"),
      ESC_WC,
    );

  if (allow) {
    if (wc !== "*") s = s.split(wc).join("*");
    s = s.replace(/\*/g, "%");
  } else {
    if (wc !== "*") s = s.split(wc).join(`\\${wc}`);
    s = s.replace(/\*/g, "\\*");
  }

  s = s.replace(new RegExp(ESC_STAR, "g"), "\\*");
  if (wc !== "*") s = s.replace(new RegExp(ESC_WC, "g"), `\\${wc}`);

  if (opts?.addContains && !s.includes("%")) s = `%${s}%`;

  return s;
}

// Compile column specs (including embeds) to PostgREST select fragments
function buildBaseColumns<T extends Tables>(
  columns: QueryOptions<T>["columns"] | undefined,
): string[] {
  if (!columns) return [];
  if (columns === "*") return ["*"];
  if (typeof columns === "string") return [columns];

  const normalized = Array.isArray(columns) ? columns : [columns];
  return normalized.map((element: unknown) => {
    if (typeof element === "string") return element;

    if (isColumnAliasSpec(element)) {
      return `${element.as ? `${element.as}:` : ""}${String(element.column)}`;
    }

    if (isEmbedSpec(element)) {
      const headBase = element.as
        ? `${element.as}:${element.table}`
        : element.table;

      const fkField = element.constraintField ?? element.constraint;
      const constraintSuffix = buildConstraintSuffix(
        element.table,
        fkField,
        element.constraintOwner ?? "embedded",
        undefined,
      );
      const head = constraintSuffix
        ? `${headBase}!${constraintSuffix}`
        : headBase;

      const innerColumnsInput = element.columns ?? ["*"];
      const normalizedInner = Array.isArray(innerColumnsInput)
        ? innerColumnsInput
        : [innerColumnsInput];

      const innerColumns = normalizedInner.map((inner: unknown) => {
        if (typeof inner === "string") return inner;

        if (isEmbedSpec(inner)) {
          const headNBase = inner.as
            ? `${inner.as}:${inner.table}`
            : inner.table;

          const nestedFkField = inner.constraintField ?? inner.constraint;
          const nestedSuffix = buildConstraintSuffix(
            inner.table,
            nestedFkField,
            inner.constraintOwner ?? "embedded",
            element.table,
          );
          const headN = nestedSuffix
            ? `${headNBase}!${nestedSuffix}`
            : headNBase;

          const colsN = Array.isArray(inner.columns)
            ? inner.columns.map((nestedCol) =>
                typeof nestedCol === "string"
                  ? nestedCol
                  : `${nestedCol.as ? `${nestedCol.as}:` : ""}${nestedCol.column}`,
              )
            : (inner.columns ?? "*");

          return `${headN}(${Array.isArray(colsN) ? colsN.join(",") : colsN})`;
        }

        if (isColumnAliasSpec(inner)) {
          return `${inner.as ? `${inner.as}:` : ""}${String(inner.column)}`;
        }

        return "*";
      });

      return `${head}(${innerColumns.join(",")})`;
    }

    return "*";
  });
}

// Build the final select string for PostgREST
function buildSelectString<T extends Tables>(
  options?: QueryOptions<T>,
  override?: QueryOptions<T>,
): string {
  const cols = override?.columns ?? options?.columns;
  const base = buildBaseColumns<T>(cols);
  const parts = [...base];
  return parts.length ? parts.join(",") : "*";
}

// Apply filter/order/limit options to embedded (joined) tables
function applyEmbeddedOptions<T extends Tables>(
  query: SelectQuery,
  columns: QueryOptions<T>["columns"] | undefined,
  parentPath?: string,
): SelectQuery {
  if (!columns) return query;
  if (columns === "*" || typeof columns === "string") return query;

  const normalized = Array.isArray(columns) ? columns : [columns];

  const qualify = (
    node: FilterExpression<Tables>,
    referencedTable: string,
  ): FilterExpression<Tables> => {
    if ("combine" in node) {
      const inner = Array.isArray(node.filter) ? node.filter : [node.filter];
      return { ...node, filter: inner.map((n) => qualify(n, referencedTable)) };
    }

    const col = node.column;
    if (
      typeof col === "object" &&
      col !== null &&
      "foreignTable" in (col as UnknownRecord)
    ) {
      return node;
    }

    return {
      ...node,
      column: { foreignTable: referencedTable, column: String(col) },
    };
  };

  for (const element of normalized) {
    if (!isEmbedSpec(element)) continue;

    const rel = String(element.as ?? element.table);
    const referencedTable = parentPath ? `${parentPath}.${rel}` : rel;

    const options = (element as UnknownRecord).options as
      | UnknownRecord
      | undefined;

    const filterRaw = options?.filter as
      | OneOrMany<FilterExpression<Tables>>
      | undefined;
    if (filterRaw) {
      const arr = Array.isArray(filterRaw) ? filterRaw : [filterRaw];
      const qualified = arr.map((n) => qualify(n, referencedTable));
      query = applyFilters<Tables>(query, qualified);
    }

    const orderByRaw = options?.orderBy as
      | OneOrMany<OrderBy<Tables>>
      | undefined;
    if (orderByRaw) {
      const orders = Array.isArray(orderByRaw) ? orderByRaw : [orderByRaw];
      for (const order of orders) {
        query = query.order(String(order.column), {
          ascending: order.ascending ?? DEFAULT_ASCENDING,
          nullsFirst: order.nullsFirst,
          referencedTable,
        });
      }
    }

    const limit = options?.limit;
    const offset = options?.offset ?? 0;
    if (Number.isFinite(limit as number) && (limit as number) > 0) {
      const from = Math.max(0, Math.trunc(offset as number));
      const to = from + Math.max(0, Math.trunc(limit as number)) - 1;
      query = query.range(from, to, { referencedTable });
    }

    const innerCols = (element as UnknownRecord).columns as
      | QueryOptions<T>["columns"]
      | undefined;
    query = applyEmbeddedOptions(query, innerCols, referencedTable);
  }

  return query;
}

// Resolve relationship suffix from a foreign field or full constraint name
function buildConstraintSuffix(
  table: string,
  constraint?: string,
  owner: ConstraintOwner = "embedded",
  parentTable?: string,
): string | undefined {
  if (!constraint) return undefined;
  if (constraint.includes("_fkey")) return constraint;
  const refTable = owner === "parent" && parentTable ? parentTable : table;
  return `${refTable}_${constraint}_fkey`;
}

// Merge filters additively (AND): base first, then overrides
function mergeFilters<T extends Tables>(
  base?: OneOrMany<FilterExpression<T>>,
  additional?: OneOrMany<FilterExpression<T>>,
): OneOrMany<FilterExpression<T>> | undefined {
  const toArr = (v?: OneOrMany<FilterExpression<T>>): FilterExpression<T>[] =>
    v ? (Array.isArray(v) ? v : [v]) : [];

  const merged = [...toArr(base), ...toArr(additional)];
  if (merged.length === 0) return undefined;

  // Deduplicate by base-table column + operator: last clause wins
  const lastIndexByKey = new Map<string, number>();
  merged.forEach((expr, idx) => {
    const maybeClause = expr as Partial<FilterClause<T>> & {
      combine?: unknown;
    };
    if (maybeClause.combine) return; // skip groups
    const col = maybeClause.column as unknown;
    const op = String((maybeClause as { op?: unknown }).op ?? "");
    // Only dedupe simple base-table columns (no foreignTable)
    if (typeof col === "string") {
      lastIndexByKey.set(`${op}|${col}`, idx);
      return;
    }
    if (
      typeof col === "object" &&
      col !== null &&
      (col as { foreignTable?: string }).foreignTable === undefined &&
      (col as { column?: string }).column
    ) {
      const key = String((col as { column?: string }).column);
      lastIndexByKey.set(`${op}|${key}`, idx);
    }
  });

  const result: FilterExpression<T>[] = [];
  merged.forEach((expr, idx) => {
    const maybeClause = expr as Partial<FilterClause<T>> & {
      combine?: unknown;
    };
    if (maybeClause.combine) {
      result.push(expr);
      return;
    }
    const col = maybeClause.column as unknown;
    const op = String((maybeClause as { op?: unknown }).op ?? "");
    if (typeof col === "string") {
      if (lastIndexByKey.get(`${op}|${col}`) === idx) result.push(expr);
      return;
    }
    if (
      typeof col === "object" &&
      col !== null &&
      (col as { foreignTable?: string }).foreignTable === undefined &&
      (col as { column?: string }).column
    ) {
      const key = String((col as { column?: string }).column);
      if (lastIndexByKey.get(`${op}|${key}`) === idx) result.push(expr);
      return;
    }
    // Non-dedupable cases (e.g., foreignTable clauses): keep as-is
    result.push(expr);
  });

  return result.length === 1 ? result[0] : result;
}

// Apply filters to a select builder
function applyFilters<T extends Tables>(
  query: SelectQuery,
  filter?: OneOrMany<FilterExpression<T>>,
): SelectQuery {
  const filters = filter ? (Array.isArray(filter) ? filter : [filter]) : [];
  if (!filters.length) return query;

  const applyClause = (q: SelectQuery, c: FilterClause<T>) => {
    const baseCol =
      typeof c.column === "string"
        ? c.column
        : typeof c.column === "object" && c.column !== null
          ? (c.column as { column: string | number | symbol }).column
          : String(c.column as string | number | symbol);
    const ftRaw =
      typeof c.column === "object" && c.column !== null
        ? (c.column as { foreignTable?: string }).foreignTable
        : undefined;
    const col = ftRaw ? `${ftRaw}.${String(baseCol)}` : String(baseCol);
    switch (c.op) {
      case "eq":
        return q.eq(col, c.value);
      case "neq":
        return q.neq(col, c.value);
      case "gt":
        return q.gt(col, c.value);
      case "gte":
        return q.gte(col, c.value);
      case "lt":
        return q.lt(col, c.value);
      case "lte":
        return q.lte(col, c.value);
      case "is":
        return q.is(col, c.value as Scalar | null | boolean);
      case "nis":
        return q.not(col, "is", c.value as Scalar | null | boolean);
      case "in":
        return q.in(col, c.value as Scalar[]);
      case "nin":
        return q.not(
          col,
          "in",
          (() => {
            const values = (
              Array.isArray(c.value) ? c.value : [c.value]
            ) as Scalar[];
            const items = values.map((v) => {
              if (v === null) return "null";
              if (typeof v === "string") return JSON.stringify(v);
              return String(v);
            });
            return `(${items.join(",")})`;
          })(),
        );
      case "like":
        return q.like(col, normalizeLike(String(c.value)));
      case "ilike":
        return q.ilike(col, normalizeLike(String(c.value)));
      case "contains":
        return q.contains(col, c.value as unknown[]);
      case "containedBy":
        return q.containedBy(col, c.value as unknown[]);
      case "overlaps":
        return q.overlaps(col, c.value as unknown[]);
      case "fts":
        return q.textSearch(col, String(c.value), { type: "plain" });
      case "phfts":
        return q.textSearch(col, String(c.value), { type: "phrase" });
      case "wfts":
        return q.textSearch(col, String(c.value), { type: "websearch" });
      case "between": {
        if (!isRange(c.value)) return q;
        const { from, to, inclusive } = c.value;
        const left = inclusive ? "gte" : "gt";
        const right = inclusive ? "lte" : "lt";
        if (left === "gte" && right === "lte") {
          return q.gte(col, from).lte(col, to);
        }
        return q.gt(col, from).lt(col, to);
      }
      case "notBetween": {
        if (!isRange(c.value)) return q;
        const { from, to, inclusive } = c.value;
        const left = inclusive ? "lt" : "lte";
        const right = inclusive ? "gt" : "gte";
        if (left === "lt" && right === "gt") {
          return q.lt(col, from).gt(col, to);
        }
        return q.lte(col, from).gte(col, to);
      }
      default:
        return q;
    }
  };

  const buildOrGroups = (nodes: FilterExpression<T>[]) => {
    const clauses = nodes.filter(
      (node): node is FilterClause<T> => !("combine" in node),
    );
    type Group = { referencedTable?: string; tokens: string[] };
    const groups = new Map<string, Group>();
    const keyOf = (refTable?: string) => refTable ?? "__base__";

    for (const clause of clauses) {
      const isObj = typeof clause.column === "object" && clause.column !== null;
      const columnOnly = isObj
        ? String((clause.column as { column: string | number | symbol }).column)
        : String(clause.column as string | number | symbol);
      const referencedTable = isObj
        ? (clause.column as { foreignTable?: string }).foreignTable
        : undefined;

      const token = (() => {
        switch (clause.op) {
          case "eq":
            return `${columnOnly}.eq.${clause.value}`;
          case "neq":
            return `${columnOnly}.neq.${clause.value}`;
          case "gt":
            return `${columnOnly}.gt.${clause.value}`;
          case "gte":
            return `${columnOnly}.gte.${clause.value}`;
          case "lt":
            return `${columnOnly}.lt.${clause.value}`;
          case "lte":
            return `${columnOnly}.lte.${clause.value}`;
          case "is":
            return `${columnOnly}.is.${clause.value}`;
          case "nis":
            return `${columnOnly}.not.is.${clause.value}`;
          case "like":
            return `${columnOnly}.like."${normalizeLike(String(clause.value))}"`;
          case "ilike":
            return `${columnOnly}.ilike."${normalizeLike(String(clause.value))}"`;
          case "fts":
            return `${columnOnly}.fts.${clause.value}`;
          case "phfts":
            return `${columnOnly}.phfts.${clause.value}`;
          case "wfts":
            return `${columnOnly}.wfts.${clause.value}`;
          default:
            return "";
        }
      })();
      if (!token) continue;

      const key = keyOf(referencedTable);
      const g = groups.get(key) ?? { referencedTable, tokens: [] };
      g.tokens.push(token);
      groups.set(key, g);
    }
    return Array.from(groups.values()).filter((g) => g.tokens.length > 0);
  };

  const applyNode = (
    queryBuilder: SelectQuery,
    node: FilterExpression<T>,
  ): SelectQuery => {
    if ("combine" in node) {
      const nodes = Array.isArray(node.filter) ? node.filter : [node.filter];
      if (!nodes.length) return queryBuilder;
      if (node.combine === "AND") return nodes.reduce(applyNode, queryBuilder);
      const groups = buildOrGroups(nodes);
      return groups.reduce(
        (acc, group) =>
          acc.or(
            group.tokens.join(","),
            group.referencedTable
              ? { referencedTable: group.referencedTable }
              : undefined,
          ),
        queryBuilder,
      );
    }
    return applyClause(queryBuilder, node);
  };

  return filters.reduce(applyNode, query);
}

// Apply search clauses by converting them into filters
function applySearch<T extends Tables>(
  query: SelectQuery,
  search?: OneOrMany<SearchClause<T>>,
): SelectQuery {
  if (!search) return query;
  const clauses = Array.isArray(search) ? search : [search];
  if (!clauses.length) return query;

  const filters: FilterExpression<T>[] = [];
  for (const s of clauses) {
    const mode = s.mode ?? "ilike";
    const raw = s.term.trim().slice(0, MAX_SEARCH_TERM_LENGTH);
    if (!raw) continue;

    const cols = Array.isArray(s.columns) ? s.columns : [s.columns];

    if (mode === "like" || mode === "ilike") {
      const wc = s.wildcardChar ?? DEFAULT_WILDCARD_CHAR;
      const allowWildcards = s.useWildcards !== false; // default: true
      const tokens = s.tokenize ? raw.split(/\s+/).filter(Boolean) : [raw];
      const combine = s.tokensCombine === "OR" ? "OR" : "AND"; // default AND
      const tokenClauses: FilterExpression<T>[] = [];
      for (let tkn of tokens) {
        if (allowWildcards) {
          if (wc && wc !== "*") tkn = tkn.split(wc).join("*");
        } else {
          if (wc && wc !== "*") tkn = tkn.split(wc).join(`\\${wc}`);
          tkn = tkn.split("*").join("\\*");
        }
        if (!tkn.startsWith("*")) tkn = `*${tkn}`;
        if (!tkn.endsWith("*")) tkn = `${tkn}*`;

        const mappedToken: FilterClause<T>[] = cols.map((c) => ({
          column: c,
          op: mode,
          value: tkn,
        }));
        if (mappedToken.length === 1) tokenClauses.push(mappedToken[0]);
        else if (mappedToken.length > 1)
          tokenClauses.push({ combine: "OR", filter: mappedToken });
      }
      if (tokenClauses.length === 1) filters.push(tokenClauses[0]);
      else if (tokenClauses.length > 1)
        filters.push({ combine, filter: tokenClauses });
    } else {
      const mapped: FilterClause<T>[] = cols.map((c) => ({
        column: c,
        op: mode,
        value: raw,
      }));
      if (mapped.length === 1) filters.push(mapped[0]);
      else if (mapped.length > 1)
        filters.push({ combine: "OR", filter: mapped });
    }
  }

  if (!filters.length) return query;
  return applyFilters<T>(query, filters);
}

// Build a select query with select/order/filter/search/pagination
function buildSelectQuery<T extends Tables>(
  client: ReturnType<typeof useSupabase>,
  table: T,
  options?: QueryOptions<T>,
  optionsOverride?: QueryOptions<T>,
): SelectQuery {
  // Select
  const count = optionsOverride?.count ?? options?.count;
  let query = (client as unknown as SupabaseClient<Database>)
    .from(table)
    .select(buildSelectString(options, optionsOverride), { count });

  // Embedded options (filters/order/limit on embeds)
  const columns = optionsOverride?.columns ?? options?.columns;
  query = applyEmbeddedOptions<T>(query, columns);

  // OrderBy
  const orderBy = optionsOverride?.orderBy ?? options?.orderBy;
  const orders = Array.isArray(orderBy) ? orderBy : orderBy ? [orderBy] : [];
  for (const order of orders) {
    query = query.order(order.column as string, {
      ascending: order.ascending ?? DEFAULT_ASCENDING,
      nullsFirst: order.nullsFirst,
    });
  }

  // Filter
  const mergedFilter = mergeFilters(options?.filter, optionsOverride?.filter);
  query = applyFilters(query, mergedFilter);

  // Search
  query = applySearch(query, optionsOverride?.search ?? options?.search);

  // Pagination
  const rawLimit = optionsOverride?.limit ?? options?.limit;
  const rawOffset = optionsOverride?.offset ?? options?.offset;

  const hasLimit = Number.isFinite(rawLimit as number);
  const limit = hasLimit
    ? Math.max(0, Math.trunc(rawLimit as number))
    : undefined;
  const offset = Number.isFinite(rawOffset as number)
    ? Math.max(0, Math.trunc(rawOffset as number))
    : 0;

  if (hasLimit && (limit as number) > 0) {
    const from = offset;
    const to = from + (limit as number) - 1;
    query = query.range(from, to);
  }

  return query;
}

// List hook backed by Supabase Cache Helpers
export function makeUseList<
  T extends Tables,
  RowIn = unknown,
  RowOut = RowIn,
>(cfg: {
  table: T;
  options?: QueryOptions<T>;
  transformItem?: (row: RowIn) => RowOut;
}): (optionsOverride?: QueryOptions<T>) => MakeUseListResult<RowOut> {
  const { table, options } = cfg;

  return function useList(
    optionsOverride?: QueryOptions<T>,
  ): MakeUseListResult<RowOut> {
    const client = useSupabase();
    const limit = optionsOverride?.limit ?? options?.limit ?? DEFAULT_LIMIT;
    const rqOptions = optionsOverride?.rqOptions ?? options?.rqOptions;
    const shouldEnable =
      (optionsOverride?.enabled ?? options?.enabled ?? true) !== false;
    const query = buildSelectQuery(client, table, options, {
      ...optionsOverride,
      limit,
    });

    const result = useQuery(query, {
      ...rqOptions,
      enabled: shouldEnable,
    });

    const items = useMemo(() => {
      const rows = (result.data ?? []) as unknown as RowIn[];
      if (!rows || rows.length === 0) return [] as RowOut[];
      if (!cfg.transformItem) return rows as unknown as RowOut[];
      const fn = cfg.transformItem;
      return fn ? rows.map((r) => fn(r)) : (rows as unknown as RowOut[]);
    }, [result.data, cfg.transformItem]);

    return { result, items };
  };
}

// Infinite list hook backed by Supabase Cache Helpers
export function makeUseInfinite<
  T extends Tables,
  RowIn = unknown,
  RowOut = RowIn,
>(cfg: {
  table: T;
  options?: QueryOptions<T>;
  transformItem?: (row: RowIn) => RowOut;
}): (optionsOverride?: QueryOptions<T>) => MakeUseInfiniteResult<RowOut> {
  const { table, options } = cfg;

  return function useInfinite(
    optionsOverride?: QueryOptions<T>,
  ): MakeUseInfiniteResult<RowOut> {
    const client = useSupabase();
    const limit =
      optionsOverride?.limit ?? options?.limit ?? DEFAULT_INFINITE_LIMIT;
    const rqOptions = optionsOverride?.rqOptions ?? options?.rqOptions;
    const shouldEnable =
      (optionsOverride?.enabled ?? options?.enabled ?? true) !== false;

    const keyOptions = {
      columns: optionsOverride?.columns ?? options?.columns,
      orderBy: optionsOverride?.orderBy ?? options?.orderBy,
      filter: mergeFilters(options?.filter, optionsOverride?.filter),
      search: optionsOverride?.search ?? options?.search,
      count: optionsOverride?.count ?? options?.count,
      limit,
    };
    const queryKey = [table, keyOptions];

    const infinite = useInfiniteQuery({
      ...rqOptions,
      queryKey,
      enabled: shouldEnable,
      initialPageParam: 0,
      getNextPageParam: (last: { items: RowOut[]; nextPage: number | null }) =>
        last.nextPage,
      queryFn: async ({ pageParam }) => {
        const page = typeof pageParam === "number" ? pageParam : 0;
        const offset = page * limit;

        const query = buildSelectQuery(client, table, options, {
          ...optionsOverride,
          limit: limit + 1,
          offset,
        });

        const { data, error } = await query;
        if (error) throw error;

        const rows = (data ?? []) as unknown as RowIn[];
        const hasMore = rows.length > limit;
        const itemsIn = hasMore ? rows.slice(0, limit) : rows;
        const items = itemsIn.map((r) =>
          cfg.transformItem ? cfg.transformItem(r) : (r as unknown as RowOut),
        );

        return { items, nextPage: hasMore ? page + 1 : null };
      },
    });

    const items = useMemo(() => {
      const pages = (infinite.data?.pages ?? []) as Array<{ items: RowOut[] }>;
      if (!pages || pages.length === 0) return [] as RowOut[];
      return pages.flatMap((p) => p.items);
    }, [infinite.data]);

    return { infinite, items };
  };
}

// Count hook backed by Supabase Cache Helpers (HEAD-only)
export function makeUseCount<T extends Tables>(cfg: {
  table: T;
  options?: QueryOptions<T>;
}): (optionsOverride?: QueryOptions<T>) => MakeUseCountResult {
  const { table, options } = cfg;

  return function useCount(
    optionsOverride?: QueryOptions<T>,
  ): MakeUseCountResult {
    const client = useSupabase();
    const rqOptions = optionsOverride?.rqOptions ?? options?.rqOptions;
    const shouldEnable =
      (optionsOverride?.enabled ?? options?.enabled ?? true) !== false;

    const selectString = buildSelectString(options, optionsOverride);
    const columns = optionsOverride?.columns ?? options?.columns;
    const mergedFilter = mergeFilters(options?.filter, optionsOverride?.filter);

    let query = (client as unknown as SupabaseClient<Database>)
      .from(table)
      .select(selectString, { count: "exact", head: true }) as SelectQuery;
    query = applyEmbeddedOptions<T>(query, columns);
    query = applyFilters(query, mergedFilter);

    const result = useQuery(query, {
      ...rqOptions,
      enabled: shouldEnable,
    });

    return { result, count: result.count ?? null };
  };
}

// Localized variants — Loyaboost v1 does NOT have the DB-i18n schema
// (`languages`, `i18n`, `localized_*` projections), so these are pure aliases.
// They become divergent makers if/when the i18n DB-layer ships (see audit §2D).
export const makeLocalizedUseList = makeUseList;
export const makeLocalizedUseInfinite = makeUseInfinite;

// Details hook backed by Supabase Cache Helpers
export function makeUseDetails<
  T extends Tables,
  RowIn = unknown,
  RowOut = RowIn,
>(cfg: {
  table: T;
  options?: QueryOptions<T>;
  transformItem?: (row: RowIn) => RowOut;
}): (override?: QueryOptions<T>) => MakeUseDetailsResult<RowOut> {
  const useList = makeUseList<T, RowIn, RowOut>(cfg);

  return (override?: QueryOptions<T>) => {
    const finalOverride: QueryOptions<T> = {
      ...override,
      rqOptions: {
        placeholderData: undefined,
        ...override?.rqOptions,
      },
    };

    const result = useList({ ...finalOverride, limit: 1 });
    return { result: result.result, item: result.items?.[0] ?? null };
  };
}
